/* =========================================================================
   MapleWeld — predictive search suggestions + search-page best match.

   Attaches to every search form carrying [data-mwh-suggest] (the inline
   desktop bar, the collapsible mobile panel, the drawer form, and the search
   page's own field) and drops a live result list under it as the customer
   types.

   WHY SEVERAL LOOKUPS (2026-10-02).
   Shopify's search tokenises "MW600" in a title as "mw" + "600", and its
   index answers inconsistently for some products: tested on this store,
   "mw600", "mw 600", "mw600*" and even the SKU "MW600L" each found the MW600
   gloves on some requests and not on others, while the Predictive Search API
   never returned it at all (it fuzzy-matched MW300 and anything with "600").
   Asking the same question several ways and merging makes the result stable:
     1. full search, as typed             "mw600"   (templates/search.mwsug)
     2. full search, letters/digits split "mw 600"  (only when different)
     3. full search, prefix wildcard      "mw600*"  (finds "mw60" mid-typing)
     4. predictive search                 products + categories, blog, pages
   Products are merged, de-duplicated and re-ranked here: exact SKU or exact
   title word first ("MW600"), then title words / SKUs that START with what was
   typed, then anything containing it, then the engines' own order. Do NOT add
   options[prefix] or options[unavailable_products] to the full-search calls:
   tested on this store, either one makes the wildcard drop the part number.

   SEARCH PAGE "BEST MATCH" (2026-10-02).
   The results page runs Shopify's plain search once, so it can miss the very
   part number searched for. On /search this script runs the same lookups and,
   if an exact or starts-with match is missing from the grid, shows it in a
   "Best match" strip above the results.

   FLOATING LIST OUTSIDE THE HEADER (2026-10-02).
   On the search page the field sits inside a hero that clips its overflow,
   so the list was cut off behind the results panel. Any form outside the
   header moves its list to <body> and pins it under the field with
   position:fixed, re-measured on scroll and resize.

   Deliberately NO PRICES in the list. Several machines carry accessories as
   variants, which makes the catalogue min price misleading.

   NOTE ON FIELD NAMES: the suggest endpoint returns a product's category as
   `type`, NOT `product_type`. Keep the fallback.

   MOBILE: the collapsible panel this can sit inside is overflow:hidden with a
   max-height cap. The panel is flagged with .mwh-sug-open while a list is
   showing; mwh-suggest.css lets it out on that flag.
   ========================================================================= */
(function () {
  'use strict';

  var MIN_CHARS = 2;
  var DEBOUNCE  = 180;
  var FETCH_N   = 8;
  var CAP = { products: 6, collections: 4, articles: 3, pages: 2 };
  var RESOURCES = 'product,collection,article,page';

  var cache = new Map();
  var seq = 0;

  function esc(v) {
    return String(v == null ? '' : v).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }

  /* Escape first, then match on the escaped strings, so the slice offsets
     stay valid when the title contains & or a quote. */
  function mark(text, q) {
    var t = esc(text);
    var n = esc(String(q || '').trim());
    if (!n) return t;
    var i = t.toLowerCase().indexOf(n.toLowerCase());
    if (i < 0) return t;
    return t.slice(0, i) + '<mark>' + t.slice(i, i + n.length) + '</mark>' + t.slice(i + n.length);
  }

  function root() {
    var r = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    return r.replace(/\/+$/, '');
  }

  function imgUrl(src, w) {
    if (!src) return '';
    if (typeof src === 'object') src = src.url || src.src || '';
    if (!src) return '';
    return src + (src.indexOf('?') > -1 ? '&' : '?') + 'width=' + w;
  }

  function meta(p) {
    return [p.vendor, p.type || p.product_type]
      .filter(function (x) { return x && String(x).trim(); })
      .join(' · ');
  }

  function norm(s) { return String(s || '').toLowerCase().replace(/[^a-z0-9]+/g, ''); }
  function path(u) { return String(u || '').split('?')[0].replace(/\/+$/, ''); }
  function split(q) {
    return String(q).replace(/([a-z])(\d)/gi, '$1 $2').replace(/(\d)([a-z])/gi, '$1 $2');
  }

  /* Full storefront search (JSON view, see templates/search.mwsug.liquid). */
  function fullSearch(term) {
    var url = root() + '/search?q=' + encodeURIComponent(term) + '&type=product&view=mwsug';
    return fetch(url, { headers: { Accept: 'application/json' } })
      .then(function (r) { if (!r.ok) throw new Error('full ' + r.status); return r.text(); })
      .then(function (t) { var j = JSON.parse(t); return (j && j.products) || []; })
      .catch(function () { return []; });
  }

  function predictive(q) {
    var url = root() + '/search/suggest.json'
      + '?q=' + encodeURIComponent(q)
      + '&resources[type]=' + RESOURCES
      + '&resources[limit]=' + FETCH_N
      + '&resources[options][unavailable_products]=last';
    return fetch(url, { headers: { Accept: 'application/json' } })
      .then(function (r) { if (!r.ok) throw new Error('suggest ' + r.status); return r.json(); })
      .then(function (j) { return (j && j.resources && j.resources.results) || {}; })
      .catch(function () { return {}; });
  }

  /* Exact part numbers and title words beat fuzzy neighbours. */
  function score(p, q) {
    var nq = norm(q);
    if (!nq) return 0;
    var s = 0;
    var words = String(p.title || '').toLowerCase().split(/[^a-z0-9]+/).filter(Boolean);
    var skus = String(p.skus || '').toLowerCase().split(/\s+/).map(norm).filter(Boolean);
    if (skus.indexOf(nq) > -1) s += 100;
    else if (skus.some(function (k) { return k.indexOf(nq) === 0; })) s += 40;
    if (words.indexOf(nq) > -1) s += 80;
    else if (words.some(function (w) { return w.indexOf(nq) === 0; })) s += 50;
    else if (norm(p.title).indexOf(nq) > -1) s += 30;
    var terms = String(q).toLowerCase().split(/\s+/).map(norm).filter(Boolean);
    if (terms.length > 1) {
      terms.forEach(function (t) {
        if (words.some(function (w) { return w.indexOf(t) === 0; })) s += 10;
      });
    }
    return s;
  }

  /* Product lookups shared by the dropdown and the search-page best match. */
  function productLookups(q) {
    var none = Promise.resolve([]);
    var sp = split(q);
    return Promise.all([
      fullSearch(q),
      sp !== q ? fullSearch(sp) : none,
      /[a-z0-9]$/i.test(q) ? fullSearch(q + '*') : none
    ]);
  }

  function rank(lists, q) {
    var seen = {}, list = [], i = 0;
    lists.forEach(function (arr) {
      (arr || []).forEach(function (p) {
        var k = path(p.url);
        if (!k || seen[k]) return;
        seen[k] = 1;
        list.push({ p: p, i: i++, s: score(p, q) });
      });
    });
    list.sort(function (a, b) { return (b.s - a.s) || (a.i - b.i); });
    return list;
  }

  function suggest(q) {
    if (cache.has(q)) return Promise.resolve(cache.get(q));

    return Promise.all([productLookups(q), predictive(q)]).then(function (res) {
      var d = res[1];
      var list = rank(res[0].concat([d.products]), q);
      var out = {
        products:    list.slice(0, CAP.products).map(function (x) { return x.p; }),
        collections: (d.collections || []).slice(0, CAP.collections),
        articles:    (d.articles    || []).slice(0, CAP.articles),
        pages:       (d.pages       || []).slice(0, CAP.pages)
      };
      cache.set(q, out);
      if (cache.size > 50) cache.delete(cache.keys().next().value);
      return out;
    });
  }

  function productRow(p, q) {
    var src = imgUrl(p.featured_image || p.image, 120);
    var m = meta(p);
    return '<a class="mwh-sug__item mwh-sug__item--prod" role="option" aria-selected="false"'
      + ' data-sug-item href="' + esc(path(p.url) || p.url) + '">'
      + '<span class="mwh-sug__th">'
      + (src
          ? '<img src="' + esc(src) + '" alt="" loading="lazy" width="52" height="52">'
          : '<span class="mwh-sug__th--none" aria-hidden="true"></span>')
      + '</span>'
      + '<span class="mwh-sug__tx">'
      + '<span class="mwh-sug__t">' + mark(p.title, q) + '</span>'
      + (m ? '<span class="mwh-sug__m">' + esc(m) + '</span>' : '')
      + (p.available === false ? '<span class="mwh-sug__oos">Out of stock</span>' : '')
      + '</span></a>';
  }

  function plainRow(item, q, cls) {
    return '<a class="mwh-sug__item mwh-sug__item--' + cls + '" role="option" aria-selected="false"'
      + ' data-sug-item href="' + esc(item.url) + '">'
      + '<span class="mwh-sug__t">' + mark(item.title, q) + '</span></a>';
  }

  function group(label, rows) {
    if (!rows) return '';
    return '<div class="mwh-sug__group"><div class="mwh-sug__lbl">' + label + '</div>' + rows + '</div>';
  }

  function build(data, q) {
    var P = data.products, C = data.collections, A = data.articles, G = data.pages;
    var total = P.length + C.length + A.length + G.length;
    var all = root() + '/search?q=' + encodeURIComponent(q) + '&type=product';

    if (!total) {
      return '<div class="mwh-sug__empty">'
        + '<strong>No matches for “' + esc(q) + '”</strong>'
        + '<span>Try a part number, a brand such as SanRex or Binzel, or a process — MIG, TIG, stick, plasma.</span>'
        + '</div>'
        + '<a class="mwh-sug__all" role="option" aria-selected="false" data-sug-item href="' + esc(all) + '">'
        + 'Search the full catalogue anyway</a>';
    }

    var left = group('Products', P.map(function (p) { return productRow(p, q); }).join(''));

    var right =
        group('Categories', C.map(function (c) { return plainRow(c, q, 'col'); }).join(''))
      + group('From the blog', A.map(function (a) { return plainRow(a, q, 'art'); }).join(''))
      + group('Pages', G.map(function (g) { return plainRow(g, q, 'page'); }).join(''));

    return '<div class="mwh-sug__cols">'
      + '<div class="mwh-sug__main">' + (left || '<div class="mwh-sug__lbl">No products match</div>') + '</div>'
      + (right ? '<div class="mwh-sug__side">' + right + '</div>' : '')
      + '</div>'
      + '<a class="mwh-sug__all" role="option" aria-selected="false" data-sug-item href="' + esc(all) + '">'
      + 'See all results for “' + esc(q) + '”'
      + '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.9" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M5 12h14M13 6l6 6-6 6"/></svg></a>';
  }

  var SKELETON =
    '<div class="mwh-sug__load" aria-hidden="true">'
    + '<span></span><span></span><span></span><span></span>'
    + '</div>';

  function attach(form) {
    var input = form.querySelector('input[name="q"]');
    if (!input || form.dataset.mwhSugReady) return;
    form.dataset.mwhSugReady = '1';

    var panel = form.closest ? form.closest('[data-mwh-search]') : null;

    /* Outside the header (search page etc.) the list floats on <body>. */
    var inHeader = !!(form.closest && form.closest('[id^="shopify-section-mwh-header"], header, .mwh-drawer'));
    var floating = !inHeader && !panel;

    var id = 'mwh-sug-' + (++seq);
    var box = document.createElement('div');
    box.className = 'mwh-sug' + (floating ? ' mwh-sug--float' : '');
    box.id = id;
    box.setAttribute('role', 'listbox');
    box.setAttribute('aria-label', 'Search suggestions');
    box.hidden = true;
    form.classList.add('mwh-sug-host');
    (floating ? document.body : form).appendChild(box);

    input.setAttribute('role', 'combobox');
    input.setAttribute('aria-autocomplete', 'list');
    input.setAttribute('aria-expanded', 'false');
    input.setAttribute('aria-controls', id);
    input.setAttribute('autocomplete', 'off');
    input.setAttribute('autocorrect', 'off');
    input.setAttribute('spellcheck', 'false');

    var timer = null;
    var token = 0;
    var cursor = -1;
    var lastQuery = '';
    var raf = 0;

    function place() {
      raf = 0;
      if (!floating || box.hidden) return;
      var vh = window.innerHeight || document.documentElement.clientHeight || 0;
      var r = form.getBoundingClientRect();
      if (vh > 0 && (r.bottom < 0 || r.top > vh)) { close(); return; }
      var top = r.bottom + 8;
      box.style.top = top + 'px';
      box.style.left = Math.max(8, r.left) + 'px';
      box.style.width = Math.max(260, Math.min(r.width, (window.innerWidth || r.width + 16) - 16)) + 'px';
      if (vh > 0) box.style.maxHeight = Math.max(220, vh - top - 16) + 'px';
    }
    function queue() { if (!raf) raf = requestAnimationFrame(place); }

    function items() {
      return Array.prototype.slice.call(box.querySelectorAll('[data-sug-item]'));
    }

    function open() {
      if (!box.hidden) { place(); return; }
      box.hidden = false;
      input.setAttribute('aria-expanded', 'true');
      if (panel) panel.classList.add('mwh-sug-open');
      if (floating) {
        place();
        window.addEventListener('scroll', queue, true);
        window.addEventListener('resize', queue);
      }
    }

    function close() {
      if (box.hidden) return;
      box.hidden = true;
      input.setAttribute('aria-expanded', 'false');
      input.removeAttribute('aria-activedescendant');
      cursor = -1;
      if (panel) panel.classList.remove('mwh-sug-open');
      if (floating) {
        window.removeEventListener('scroll', queue, true);
        window.removeEventListener('resize', queue);
      }
    }

    function move(step) {
      var list = items();
      if (!list.length) return;
      if (cursor > -1 && list[cursor]) {
        list[cursor].classList.remove('is-cursor');
        list[cursor].setAttribute('aria-selected', 'false');
      }
      cursor += step;
      if (cursor < 0) cursor = list.length - 1;
      if (cursor >= list.length) cursor = 0;
      var el = list[cursor];
      if (!el.id) el.id = id + '-o' + cursor;
      el.classList.add('is-cursor');
      el.setAttribute('aria-selected', 'true');
      input.setAttribute('aria-activedescendant', el.id);
      if (el.scrollIntoView) el.scrollIntoView({ block: 'nearest' });
    }

    function run(q) {
      var mine = ++token;
      box.innerHTML = SKELETON;
      open();
      suggest(q)
        .then(function (data) {
          if (mine !== token) return;
          if (input.value.trim() !== q) return;
          box.innerHTML = build(data, q);
          cursor = -1;
          open();
        })
        .catch(function () {
          if (mine !== token) return;
          close();
        });
    }

    input.addEventListener('input', function () {
      var q = input.value.trim();
      clearTimeout(timer);
      if (q.length < MIN_CHARS) { token++; close(); lastQuery = ''; return; }
      if (q === lastQuery && !box.hidden) return;
      lastQuery = q;
      timer = setTimeout(function () { run(q); }, DEBOUNCE);
    });

    input.addEventListener('focus', function () {
      var q = input.value.trim();
      if (q.length >= MIN_CHARS && cache.has(q)) {
        box.innerHTML = build(cache.get(q), q);
        cursor = -1;
        open();
      }
    });

    input.addEventListener('keydown', function (e) {
      if (e.key === 'Escape') { close(); return; }
      if (box.hidden) return;
      if (e.key === 'ArrowDown') { e.preventDefault(); move(1); }
      else if (e.key === 'ArrowUp') { e.preventDefault(); move(-1); }
      else if (e.key === 'Enter') {
        var list = items();
        if (cursor > -1 && list[cursor]) { e.preventDefault(); window.location.href = list[cursor].href; }
      }
      else if (e.key === 'Tab') { close(); }
    });

    form.addEventListener('submit', close);
    box.addEventListener('mousedown', function (e) { e.stopPropagation(); });
    box.addEventListener('touchstart', function (e) { e.stopPropagation(); }, { passive: true });

    document.addEventListener('click', function (e) {
      if (!form.contains(e.target) && !box.contains(e.target)) close();
    });

    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && !box.hidden) close();
    });
  }

  /* ---------- search page: surface the exact match the grid missed ---------- */
  function bestMatch() {
    if (!/\/search\/?$/.test(location.pathname)) return;
    if (document.querySelector('[data-mwh-best]')) return;
    var q = (new URLSearchParams(location.search).get('q') || '').replace(/\*+$/, '').trim();
    if (q.length < MIN_CHARS) return;
    var grid = document.querySelector('.mwk__grid');
    var host = grid ? grid.parentNode : document.querySelector('.mwk__body .mwk__wrap, .mwk__body');
    if (!host) return;
    var shown = {};
    if (grid) {
      Array.prototype.forEach.call(grid.querySelectorAll('a[href*="/products/"]'), function (a) {
        shown[path(a.getAttribute('href'))] = 1;
      });
    }
    productLookups(q).then(function (res) {
      if (document.querySelector('[data-mwh-best]')) return;
      var best = rank(res, q).filter(function (x) {
        return x.s >= 50 && !shown[path(x.p.url)];
      }).slice(0, 3);
      if (!best.length) return;
      var html = '<div class="mwh-best" data-mwh-best>'
        + '<div class="mwh-best__lbl">Best match for “' + esc(q) + '”</div>'
        + '<div class="mwh-best__row">'
        + best.map(function (x) {
            var p = x.p, src = imgUrl(p.image, 160), m = meta(p);
            return '<a class="mwh-best__item" href="' + esc(path(p.url)) + '">'
              + '<span class="mwh-best__th">' + (src ? '<img src="' + esc(src) + '" alt="" width="64" height="64" loading="lazy">' : '') + '</span>'
              + '<span class="mwh-best__tx"><b>' + mark(p.title, q) + '</b>' + (m ? '<small>' + esc(m) + '</small>' : '') + '</span>'
              + '<span class="mwh-best__go">View<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M5 12h14M13 6l6 6-6 6"/></svg></span>'
              + '</a>';
          }).join('')
        + '</div></div>';
      var wrap = document.createElement('div');
      wrap.innerHTML = html;
      host.insertBefore(wrap.firstChild, grid || host.firstChild);
    });
  }

  function init() {
    document.querySelectorAll('form[data-mwh-suggest]').forEach(attach);
    bestMatch();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  /* The theme editor re-renders a whole section on every settings change;
     attach() and bestMatch() are guarded, so re-running is free. */
  document.addEventListener('shopify:section:load', init);
})();